import React, { useReducer, useCallback, useMemo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  FlaskConical,
  User,
  Clock,
  CreditCard,
  ShieldCheck,
  Landmark,
  FileText,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  X,
  Upload,
  Loader2,
  MapPin,
  Crosshair,
  Search,
  History,
} from "lucide-react";
import {
  markDirty,
  resetWizard as resetWizardAction,
  fetchWorkflowChain,
  saveWizardStep,
  fetchDraft,
  fetchDraftProgress,
  fetchDetail,
  submitOnboarding,
  updateOnboarding,
  selectCcOnboarding,
  selectPendingEdit,
  beginEdit,
} from "../../store/ccOnboardingSlice";
import PendingEditNotice from "./pendingEditNotice";
import ApprovalHistoryTable, { extractApprovalHistory } from "./ApprovalHistoryTable";
import { STEP_ID_TO_NO, mapDraftToForm } from "../../utils/ccOnboardingPayloadMapper";
import useNavigationGuard, { useConfirmedNavigate } from "../../hooks/useNavigationGuard";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import ConfirmLeaveModal from "../common/ConfirmLeaveModal";
import { toast } from "react-toastify";
import ccOnboardingApi, {
  USER_ROLE_OPTIONS,
  DIAGNOSTIC_TYPE_OPTIONS,
  YES_NO_OPTIONS,
  TOTAL_APPROVAL_LEVELS,
  STATUS_CODES,
  ATTACHMENT_SLOTS,
  currentUserCode,
} from "../../services/ccOnboardingApi";
import { viewGcpDocument } from "../../services/actions";

const DIRTYING_ACTION_TYPES = new Set([
  "SET_FIELD",
  "ADD_CONTACT",
  "REMOVE_CONTACT",
  "UPDATE_CONTACT",
  "ADD_TIMING",
  "REMOVE_TIMING",
  "UPDATE_TIMING",
  "ADD_ATTACHMENT",
  "REMOVE_ATTACHMENT",
  "SET_ATTACHMENT_FILE",
  "UPDATE_ATTACHMENT",
  "SET_DOCUMENT_FILE",
  "ADD_LAB_ROW",
  "REMOVE_LAB_ROW",
  "UPDATE_LAB_ROW",
]);

const STEPS = [
  { id: "labInfo", label: "Collection Center", icon: FlaskConical },
  { id: "contacts", label: "Contact Persons", icon: User },
  { id: "testTimings", label: "Test & Timings", icon: Clock },
  { id: "payment", label: "Payment & Commission", icon: CreditCard },
  { id: "compliance", label: "Compliance & Registration", icon: ShieldCheck },
  { id: "bank", label: "Bank Details", icon: Landmark },
  { id: "documents", label: "Documents & Settings", icon: FileText },
  { id: "approvalHistory", label: "Approval History", icon: History },
];

const REQUIRED_FIELDS = {
  labInfo: ["labName", "address", "city", "state", "pinCode", "userRole", "diagnosticType"],
  contacts: [],
  testTimings: [],
  payment: ["paymentPeriod"],
  compliance: [],
  bank: [],
  documents: [],
  approvalHistory: [],
};

const FIELD_LABELS = {
  labName: "CC Name",
  mobileNumber: "Mobile Number",
  address: "Address",
  city: "City",
  state: "State",
  pinCode: "Pin Code",
  zone: "Zone",
  circle: "Circle",
  userRole: "User Role",
  diagnosticType: "Diagnostic Type",
  paymentPeriod: "Payment Period",
  bankName: "Bank Name",
  accountHolderName: "Account Holder Name",
  accountNumber: "Account Number",
  ifscCode: "IFSC Code",
  contractCopy: "Agreement Copy",
  bankProof: "Bank Proof",
  kycDocument: "KYC Document",
  panCard: "PAN Card Document",
  panNumber: "PAN Number",
  smPhoto: "SM Photo",
  email: "Email ID",
};

function validateContacts(contacts) {
  const errors = [];
  contacts.forEach((contact, index) => {
    const prefix = `Contact #${index + 1}`;

    if (!contact.firstName || contact.firstName.trim() === "") {
      errors.push(`${prefix}: First Name is required`);
    }

    if (!contact.lastName || contact.lastName.trim() === "") {
      errors.push(`${prefix}: Last Name is required`);
    }

    if (!contact.mobileNumber || contact.mobileNumber.trim() === "") {
      errors.push(`${prefix}: Mobile Number is required`);
    } else {
      const cleanNumber = contact.mobileNumber.replace(/\D/g, "");
      if (cleanNumber.length !== 10) {
        errors.push(`${prefix}: Mobile Number must be exactly 10 digits`);
      }
    }

    if (!contact.email || contact.email.trim() === "") {
      errors.push(`${prefix}: Email ID is required`);
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contact.email.trim())) {
        errors.push(`${prefix}: Enter a valid Email address`);
      }
    }

    if (!contact.panNumber || contact.panNumber.trim() === "") {
      errors.push(`${prefix}: PAN Number is required`);
    } else {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panRegex.test(contact.panNumber.toUpperCase())) {
        errors.push(`${prefix}: Please enter a valid PAN (e.g., ABCDE1234F)`);
      }
    }

    if (!contact.smPhoto && !contact.remoteSmPhoto) {
      errors.push(`${prefix}: SM Photo is required`);
    }
  });
  return errors;
}

const emptyContact = () => ({
  id: Date.now() + Math.random(),
  salutation: "",
  firstName: "",
  lastName: "",
  designation: "",
  mobileNumber: "",
  email: "",
  gender: "",
  dob: "",
  source: "",
  qualification: "",
  address1: "",
  city: "",
  state: "",
  pinCode: "",
  country: "India",
  panNumber: "",
  smPhoto: null,
  remoteSmPhoto: null,
});

const emptyTiming = (
  defaultDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  opensAt = "08:00",
  closesAt = "17:00"
) => ({
  id: Date.now() + Math.random(),
  days: defaultDays || [],
  opensAt: opensAt || "08:00",
  closesAt: closesAt || "17:00",
  isClosed: false,
});

const emptyAttachment = () => ({
  id: Date.now() + Math.random(),
  partnerName: "",
  file: null,
  remoteFileUrl: null,
  remoteFileName: null,
});

const emptyLabRow = () => ({
  id: Date.now() + Math.random(),
  labCode: "",
  labName: "",
  commission: "",
});

const getTodayDate = () => new Date().toISOString().split("T")[0];

const MOBILAB_AVAILABILITY_OPTIONS = [
  { label: "CBC", value: "CBC" },
  { label: "Mobilab", value: "Mobilab" },
  { label: "Mobilab + CBC", value: "Mobilab + CBC" },
  { label: "None", value: "None" },
];

const SEND_REPORT_TO_PATIENT_OPTIONS = [
  { label: "NO", value: 0 },
  { label: "YES", value: 1 },
];

const initialState = {
  currentStep: 0,
  status: "idle",
  errorMessage: "",
  form: {
    labInfo: {
      labName: "",
      mobileNumber: "",
      emailId: "",
      location: "",
      area: "",
      latitude: "",
      longitude: "",
      address: "",
      city: "",
      state: "",
      stateCode: "",
      pinCode: "",
      zone: "",
      zoneCode: "",
      circle: "",
      circleCode: "",
      country: "India",
      receptionNumber: "",
      agreementDate: getTodayDate(),
      terminationNoticePeriod: "",
      userRole: "",
      eclinicCode: "",
      eclinicName: "",
      corporateCode: "",
      corporateName: "",
      mobilabId: "",
      diagnosticType: 3,
      automapping: 1,
      sendReportToPatient: 0,
      mobilabAvailabity: "",
      mobilabAvailability: "",
    },
    contacts: [emptyContact()],
    testTimings: {
      timings: [emptyTiming()],
      attachments: [],
    },
    payment: {
      paymentPeriod: "",
      paymentReconciliation: "",
      isCureBayLabCenter: 1,
      radiologyLabs: [],
      preferredRadiologyLabs: [],
      pathologyLabs: [],
      preferredPathologyLabs: [],
    },
    compliance: {
      gstNumber: "",
      nablNumber: "",
    },
    bank: {
      bankName: "",
      accountHolderName: "",
      accountNumber: "",
      ifscCode: "",
      branchLocation: "",
    },
    documents: {
      contractCopy: null,
      bankProof: null,
      labLogo: null,
      kycDocument: null,
      panCard: null,
      remote: {},
    },
  },
};

const getSalutationOptions = () => ["Mr.", "Ms.", "Mrs.", "Dr."];

function missingRequired(sectionId, data) {
  const req = REQUIRED_FIELDS[sectionId] || [];
  return req.filter((k) => {
    const val = data ? data[k] : undefined;
    return val === "" || val === null || val === undefined;
  });
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, currentStep: action.payload, errorMessage: "" };
    case "GO_NEXT":
      return { ...state, currentStep: Math.min(state.currentStep + 1, STEPS.length - 1), errorMessage: "" };
    case "GO_BACK":
      return { ...state, currentStep: Math.max(state.currentStep - 1, 0), errorMessage: "" };
    case "SET_FIELD": {
      const { section, field, value } = action.payload;
      return {
        ...state,
        form: {
          ...state.form,
          [section]: {
            ...state.form[section],
            [field]: value,
          },
        },
      };
    }
    case "ADD_CONTACT":
      return {
        ...state,
        form: {
          ...state.form,
          contacts: [...state.form.contacts, emptyContact()],
        },
      };
    case "REMOVE_CONTACT":
      return {
        ...state,
        form: {
          ...state.form,
          contacts: state.form.contacts.filter((c) => c.id !== action.payload),
        },
      };
    case "UPDATE_CONTACT": {
      const { id, field, value } = action.payload;
      return {
        ...state,
        form: {
          ...state.form,
          contacts: state.form.contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
        },
      };
    }
    case "ADD_TIMING": {
      const existingTimings = state.form.testTimings?.timings || [];
      const firstSlot = existingTimings[0] || {};
      const defaultOpensAt = firstSlot.opensAt || "08:00";
      const defaultClosesAt = firstSlot.closesAt || "17:00";

      const existingAssigned = new Set();
      existingTimings.forEach((t) => {
        const dList = Array.isArray(t.days) && t.days.length ? t.days : t.day ? [t.day] : [];
        dList.forEach((d) => existingAssigned.add(d));
      });
      const allDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
      const remainingDays = allDays.filter((d) => !existingAssigned.has(d));
      return {
        ...state,
        form: {
          ...state.form,
          testTimings: {
            ...state.form.testTimings,
            timings: [
              ...existingTimings,
              emptyTiming(remainingDays, defaultOpensAt, defaultClosesAt),
            ],
          },
        },
      };
    }
    case "SET_TIMINGS":
      return {
        ...state,
        form: {
          ...state.form,
          testTimings: {
            ...state.form.testTimings,
            timings: action.payload,
          },
        },
      };
    case "REMOVE_TIMING":
      return {
        ...state,
        form: {
          ...state.form,
          testTimings: {
            ...state.form.testTimings,
            timings: state.form.testTimings.timings.filter((t) => t.id !== action.payload),
          },
        },
      };
    case "UPDATE_TIMING": {
      const { id, field, value } = action.payload;
      return {
        ...state,
        form: {
          ...state.form,
          testTimings: {
            ...state.form.testTimings,
            timings: state.form.testTimings.timings.map((t) => (t.id === id ? { ...t, [field]: value } : t)),
          },
        },
      };
    }
    case "ADD_ATTACHMENT":
      return {
        ...state,
        form: {
          ...state.form,
          testTimings: {
            ...state.form.testTimings,
            attachments: [...state.form.testTimings.attachments, emptyAttachment()],
          },
        },
      };
    case "REMOVE_ATTACHMENT":
      return {
        ...state,
        form: {
          ...state.form,
          testTimings: {
            ...state.form.testTimings,
            attachments: state.form.testTimings.attachments.filter((a) => a.id !== action.payload),
          },
        },
      };
    case "SET_ATTACHMENT_FILE": {
      const { id, file } = action.payload;
      return {
        ...state,
        form: {
          ...state.form,
          testTimings: {
            ...state.form.testTimings,
            attachments: state.form.testTimings.attachments.map((a) => (a.id === id ? { ...a, file } : a)),
          },
        },
      };
    }
    case "UPDATE_ATTACHMENT": {
      const { id, field, value } = action.payload;
      return {
        ...state,
        form: {
          ...state.form,
          testTimings: {
            ...state.form.testTimings,
            attachments: state.form.testTimings.attachments.map((a) => (a.id === id ? { ...a, [field]: value } : a)),
          },
        },
      };
    }
    case "SET_DOCUMENT_FILE": {
      const { field, file } = action.payload;
      return { ...state, form: { ...state.form, documents: { ...state.form.documents, [field]: file } } };
    }
    case "ADD_LAB_ROW": {
      const group = typeof action.payload === "string" ? action.payload : action.payload?.group;
      if (!group) return state;
      const currentList = Array.isArray(state.form?.payment?.[group]) ? state.form.payment[group] : [];
      return {
        ...state,
        form: {
          ...state.form,
          payment: { ...state.form?.payment, [group]: [...currentList, emptyLabRow()] },
        },
      };
    }
    case "REMOVE_LAB_ROW": {
      const group = typeof action.payload === "object" ? action.payload?.group : null;
      const id = typeof action.payload === "object" ? action.payload?.id : null;
      if (!group) return state;
      const currentList = Array.isArray(state.form?.payment?.[group]) ? state.form.payment[group] : [];
      return {
        ...state,
        form: {
          ...state.form,
          payment: { ...state.form?.payment, [group]: currentList.filter((r) => r.id !== id) },
        },
      };
    }
    case "UPDATE_LAB_ROW": {
      const { group, id, field, value } = action.payload || {};
      if (!group) return state;
      const currentList = Array.isArray(state.form?.payment?.[group]) ? state.form.payment[group] : [];
      return {
        ...state,
        form: {
          ...state.form,
          payment: {
            ...state.form?.payment,
            [group]: currentList.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
          },
        },
      };
    }
    case "SUBMIT_START":
      return { ...state, status: "submitting", errorMessage: "" };
    case "SUBMIT_SUCCESS":
      return { ...state, status: "success" };
    case "SUBMIT_ERROR":
      return { ...state, status: "error", errorMessage: action.payload };
    case "HYDRATE_FORM": {
      const incoming = action.payload || {};
      const nextForm = { ...state.form };
      Object.entries(incoming).forEach(([section, value]) => {
        if (value === undefined) return;
        if (section === "labInfo" && value.agreementDate === undefined) {
          value = { ...value, agreementDate: value.agreementDate || getTodayDate() };
        }
        nextForm[section] = section === "contacts" ? value : { ...state.form[section], ...value };
      });
      return { ...state, form: nextForm };
    }
    case "CLEAR_ERROR":
      return { ...state, errorMessage: "" };
    case "RESET_FORM":
      return initialState;
    default:
      return state;
  }
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={`${className} text-left`}>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-400 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100";

function TextInput(props) {
  return <input {...props} className={inputCls} />;
}

function SelectInput({ children, ...props }) {
  return (
    <select {...props} className={inputCls}>
      {children}
    </select>
  );
}

export function DateInput({ value, onChange, disabled = false, maxDate, minDate, placeholder = "YYYY-MM-DD", compact = false }) {
  const dayjsValue = value ? dayjs(value) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={dayjsValue}
        disabled={disabled}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        minDate={minDate ? dayjs(minDate) : undefined}
        format="YYYY-MM-DD"
        onChange={(newDate) => {
          if (newDate && dayjs.isDayjs(newDate) && newDate.isValid()) {
            onChange(newDate.format("YYYY-MM-DD"));
          } else {
            onChange("");
          }
        }}
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            placeholder: placeholder,
            sx: {
              "& .MuiOutlinedInput-root": {
                height: compact ? 34 : undefined,
                borderRadius: "0.5rem",
                backgroundColor: disabled ? "#f8fafc" : "#ffffff",
                fontSize: compact ? "0.8125rem" : "0.875rem",
                fontFamily: "inherit",
                "& fieldset": {
                  borderColor: "#94a3b8",
                },
                "&:hover fieldset": {
                  borderColor: "#6366f1",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#4f46e5",
                  borderWidth: "1.5px",
                },
              },
              "& .MuiInputBase-input": {
                padding: compact ? "6px 10px" : "9px 12px",
                fontSize: compact ? "0.8125rem" : "0.875rem",
                color: "#0f172a",
                fontFamily: "inherit",
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

export function SearchableSelect({
  value,
  options = [],
  onChange,
  placeholder = "Select",
  searchPlaceholder = "Type to search…",
  loading = false,
  disabled = false,
  getLabel,
  getKey,
  getSubtitle,
  stateFilter,
  compact = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const resolveLabel = (opt) => {
    if (!opt) return "";
    if (getLabel) return getLabel(opt);
    if (typeof opt === "string") return opt;
    return opt.label || opt.name || opt.description || opt.circleName || opt.stateName || opt.hospitalName || opt.eclinicName || opt.corporateName || String(opt.code || opt.id || "");
  };

  const resolveKey = (opt) => {
    if (!opt) return "";
    if (getKey) return String(getKey(opt));
    if (typeof opt === "string") return opt;
    return String(opt.value ?? opt.code ?? opt.id ?? opt.hospitalCode ?? opt.eclinicCode ?? opt.corporateCode ?? resolveLabel(opt));
  };

  const selectedOption = useMemo(() => {
    if (value === "" || value === null || value === undefined) return null;
    return (
      options.find((o) => {
        const k = resolveKey(o);
        const l = resolveLabel(o);
        return String(k) === String(value) || String(l) === String(value);
      }) || null
    );
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    let list = Array.isArray(options) ? [...options] : [];
    if (stateFilter && !search.trim()) {
      const matchState = String(stateFilter).toLowerCase();
      const stateFiltered = list.filter((o) => {
        if (!o || typeof o !== "object") return true;
        const st = String(o.state || o.stateName || "").toLowerCase();
        return !st || st.includes(matchState);
      });
      if (stateFiltered.length > 0) list = stateFiltered;
    }
    if (!search.trim()) {
      return list.sort((a, b) => resolveLabel(a).localeCompare(resolveLabel(b)));
    }
    const query = search.trim().toLowerCase();
    const matched = list.filter((o) => {
      const label = resolveLabel(o).toLowerCase();
      const key = resolveKey(o).toLowerCase();
      const sub = getSubtitle ? String(getSubtitle(o)).toLowerCase() : "";
      const city = typeof o === "object" ? String(o.city || "").toLowerCase() : "";
      const state = typeof o === "object" ? String(o.state || o.stateName || "").toLowerCase() : "";
      return (
        label.includes(query) ||
        key.includes(query) ||
        sub.includes(query) ||
        city.includes(query) ||
        state.includes(query)
      );
    });

    // Intelligent prefix/exact ranking
    return matched.sort((a, b) => {
      const labelA = resolveLabel(a).toLowerCase();
      const labelB = resolveLabel(b).toLowerCase();
      const codeA = resolveKey(a).toLowerCase();
      const codeB = resolveKey(b).toLowerCase();
      const aStarts = labelA.startsWith(query) || codeA.startsWith(query);
      const bStarts = labelB.startsWith(query) || codeB.startsWith(query);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return labelA.localeCompare(labelB);
    });
  }, [options, search, stateFilter]);

  const displayLabel = selectedOption ? resolveLabel(selectedOption) : value ? String(value) : "";

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`flex w-full items-center justify-between rounded-lg border bg-white ${compact ? 'px-2.5 py-1 text-xs h-[34px]' : 'px-3 py-2.5 text-sm'} transition ${
          disabled ? "cursor-not-allowed bg-slate-100 opacity-60 border-slate-300" : "cursor-pointer border-slate-400 hover:border-indigo-400"
        } ${isOpen ? "border-indigo-500 ring-2 ring-indigo-100" : ""}`}
      >
        <span className={`truncate text-left ${displayLabel ? "font-medium text-slate-900" : "text-slate-400"}`}>
          {loading ? "Loading…" : displayLabel || placeholder}
        </span>
        <div className="flex items-center gap-1.5">
          {value && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange("", null);
                setSearch("");
              }}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </div>
      </div>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-72 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-xs outline-none focus:border-indigo-400"
              />
            </div>
            {stateFilter && (
              <p className="mt-1 text-[11px] text-indigo-600">
                Filtered by state: <span className="font-semibold">{stateFilter}</span>
              </p>
            )}
          </div>

          <div className="max-h-52 overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">
                No matching options found
              </div>
            ) : (
              filteredOptions.map((o) => {
                const k = resolveKey(o);
                const isSelected = String(value) === k || String(value) === resolveLabel(o);
                const label = resolveLabel(o);
                const subtitle = getSubtitle ? getSubtitle(o) : typeof o === "object" ? o.hospitalCode || o.eclinicCode || o.code : null;

                return (
                  <div
                    key={k}
                    onClick={() => {
                      onChange(k, o);
                      setIsOpen(false);
                      setSearch("");
                    }}
                    className={`flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-xs transition ${
                      isSelected
                        ? "bg-indigo-50 font-semibold text-indigo-700"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <div>
                      <div className="font-medium text-slate-800">{label}</div>
                      {subtitle && (
                        <div className="text-[11px] text-slate-400">{subtitle}</div>
                      )}
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-indigo-600" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LocationMapModal({ initialValue, initialCoords, onClose, onConfirm }) {
  const parsed = useMemo(() => {
    if (
      initialCoords &&
      !isNaN(initialCoords.lat) &&
      !isNaN(initialCoords.lng) &&
      Number(initialCoords.lat) !== 0 &&
      Number(initialCoords.lng) !== 0
    ) {
      return { lat: parseFloat(initialCoords.lat), lng: parseFloat(initialCoords.lng) };
    }
    const m = /(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/.exec(initialValue || "");
    return m ? { lat: parseFloat(m[1]), lng: parseFloat(m[2]) } : { lat: 20.3558, lng: 85.8168 };
  }, [initialValue, initialCoords]);

  const [coords, setCoords] = useState(parsed);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addressPreview, setAddressPreview] = useState(null);
  const [fetchingAddress, setFetchingAddress] = useState(false);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const searchContainerRef = useRef(null);

  const fetchAddressDetails = useCallback(async (lat, lng) => {
    setFetchingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
      );
      if (response.ok) {
        const data = await response.json();
        const addr = data.address || {};
        const areaName =
          addr.suburb ||
          addr.neighbourhood ||
          addr.residential ||
          addr.road ||
          addr.village ||
          addr.town ||
          addr.city ||
          "";
        const parsedData = {
          address: data.display_name || "",
          location: areaName,
          area: areaName,
          city: addr.city || addr.town || addr.village || addr.county || addr.state_district || "",
          state: addr.state || "",
          pinCode: addr.postcode || "",
          latitude: lat,
          longitude: lng,
        };
        setAddressPreview(parsedData);
        return parsedData;
      }
    } catch {
      // ignore
    } finally {
      setFetchingAddress(false);
    }
    return null;
  }, []);

  // Update map and marker view
  const panToCoords = useCallback((lat, lng, zoom = 16) => {
    setCoords({ lat, lng });
    if (mapInstanceRef.current && markerRef.current) {
      mapInstanceRef.current.setView([lat, lng], zoom);
      markerRef.current.setLatLng([lat, lng]);
    }
    fetchAddressDetails(lat, lng);
  }, [fetchAddressDetails]);

  // Dynamically load Leaflet and initialize map
  useEffect(() => {
    let active = true;

    const loadLeaflet = async () => {
      if (!window.L) {
        if (!document.getElementById("leaflet-css")) {
          const link = document.createElement("link");
          link.id = "leaflet-css";
          link.rel = "stylesheet";
          link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
          document.head.appendChild(link);
        }

        if (!document.getElementById("leaflet-js")) {
          const script = document.createElement("script");
          script.id = "leaflet-js";
          script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
          document.body.appendChild(script);
          await new Promise((resolve) => {
            script.onload = resolve;
          });
        } else {
          while (!window.L) {
            await new Promise((r) => setTimeout(r, 100));
          }
        }
      }

      if (!active || !mapContainerRef.current) return;
      const L = window.L;
      if (!L) return;

      if (!mapInstanceRef.current && mapContainerRef.current) {
        const initialZoom = initialValue ? 15 : 5;
        const map = L.map(mapContainerRef.current).setView([coords.lat, coords.lng], initialZoom);
        mapInstanceRef.current = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "&copy; OpenStreetMap contributors",
          maxZoom: 19,
        }).addTo(map);

        const customIcon = L.icon({
          iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
          shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41],
        });

        const marker = L.marker([coords.lat, coords.lng], {
          draggable: true,
          icon: customIcon,
        }).addTo(map);
        markerRef.current = marker;

        marker.on("dragend", (e) => {
          const latlng = e.target.getLatLng();
          const lat = parseFloat(latlng.lat.toFixed(6));
          const lng = parseFloat(latlng.lng.toFixed(6));
          setCoords({ lat, lng });
          fetchAddressDetails(lat, lng);
        });

        map.on("click", (e) => {
          const lat = parseFloat(e.latlng.lat.toFixed(6));
          const lng = parseFloat(e.latlng.lng.toFixed(6));
          marker.setLatLng([lat, lng]);
          setCoords({ lat, lng });
          fetchAddressDetails(lat, lng);
        });

        setTimeout(() => {
          map.invalidateSize();
        }, 200);

        if (initialValue) {
          fetchAddressDetails(coords.lat, coords.lng);
        }
      }
    };

    loadLeaflet();

    return () => {
      active = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, [coords.lat, coords.lng, fetchAddressDetails, initialValue]);

  // Search by location name (debounced)
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            searchQuery.trim()
          )}&limit=5&addressdetails=1`
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data || []);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close search suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectSearchResult = (item) => {
    const lat = parseFloat(parseFloat(item.lat).toFixed(6));
    const lng = parseFloat(parseFloat(item.lon).toFixed(6));
    panToCoords(lat, lng, 16);
    setSearchQuery("");
    setSearchResults([]);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by this browser.");
      return;
    }
    setLocating(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = parseFloat(pos.coords.latitude.toFixed(6));
        const lng = parseFloat(pos.coords.longitude.toFixed(6));
        panToCoords(lat, lng, 16);
        setLocating(false);
      },
      () => {
        setError("Could not fetch location. Please check browser permissions or search by location name.");
        setLocating(false);
      }
    );
  };

  const handleConfirm = async () => {
    const locStr = `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
    const addressData = addressPreview || (await fetchAddressDetails(coords.lat, coords.lng));
    onConfirm(locStr, addressData, coords);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-[15px] font-bold text-slate-900">
              <MapPin className="h-4 w-4 text-indigo-600" /> Pin & Select Location on Map
            </h3>
            <p className="text-xs text-slate-500">
              Search a place, drag the pin, or click anywhere on the map to set coordinates.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Location Search Bar */}
        <div className="relative z-50 mb-3" ref={searchContainerRef}>
          <div className="relative flex items-center">
            <Search className="absolute left-3 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by location, area, city or landmark…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-10 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            {searching ? (
              <Loader2 className="absolute right-3 h-4 w-4 animate-spin text-indigo-600" />
            ) : searchQuery ? (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setSearchResults([]);
                }}
                className="absolute right-3 text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute left-0 top-full z-[1001] mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-2xl">
              {searchResults.map((item) => (
                <div
                  key={item.place_id}
                  onClick={() => handleSelectSearchResult(item)}
                  className="flex cursor-pointer items-start gap-2.5 border-b border-slate-100 p-2.5 text-xs text-slate-700 transition hover:bg-indigo-50 hover:text-indigo-900 last:border-none"
                >
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-500" />
                  <span className="line-clamp-2 leading-relaxed">{item.display_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Interactive Map Container */}
        <div className="relative z-0 h-72 w-full overflow-hidden rounded-xl border border-slate-300 bg-slate-100">
          <div ref={mapContainerRef} className="h-full w-full" />
          <div className="pointer-events-none absolute bottom-2 left-2 z-[400] rounded-md bg-white/90 px-2 py-1 text-[11px] font-medium text-slate-700 shadow backdrop-blur-sm">
            📍 Drag pin or click map to reposition
          </div>
        </div>

        {/* Action Controls & Lat/Lng Inputs */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={useCurrentLocation}
            disabled={locating}
            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:opacity-60"
          >
            {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
            Use current location
          </button>

          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-semibold">Lat:</span>
            <input
              type="number"
              step="any"
              value={coords.lat}
              onChange={(e) => {
                const lat = parseFloat(e.target.value) || 0;
                panToCoords(lat, coords.lng);
              }}
              className="w-24 rounded border border-slate-300 px-2 py-1 text-xs outline-none focus:border-indigo-400"
            />
            <span className="font-semibold">Lng:</span>
            <input
              type="number"
              step="any"
              value={coords.lng}
              onChange={(e) => {
                const lng = parseFloat(e.target.value) || 0;
                panToCoords(coords.lat, lng);
              }}
              className="w-24 rounded border border-slate-300 px-2 py-1 text-xs outline-none focus:border-indigo-400"
            />
          </div>
        </div>

        {/* Address Preview Box */}
        {(addressPreview || fetchingAddress) && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-700">
            <div className="flex items-center justify-between font-semibold text-slate-900">
              <span>Captured Address Details:</span>
              {fetchingAddress && <span className="flex items-center gap-1 text-[11px] font-normal text-indigo-600"><Loader2 className="h-3 w-3 animate-spin" /> Fetching address…</span>}
            </div>
            {addressPreview && (
              <div className="mt-1 space-y-0.5">
                <div className="line-clamp-2 text-slate-600">{addressPreview.address}</div>
                <div className="text-[11px] text-slate-500">
                  {[addressPreview.city, addressPreview.state, addressPreview.pinCode].filter(Boolean).join(" • ")}
                </div>
              </div>
            )}
          </div>
        )}

        {error && <p className="mt-2 text-xs text-rose-500">{error}</p>}

        <div className="mt-4 flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700"
          >
            Confirm Location & Address
          </button>
        </div>
      </div>
    </div>
  );
}

function LocationPicker({ value, latitude, longitude, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const displayLabel = useMemo(() => {
    const lat = latitude !== undefined && latitude !== null ? String(latitude).trim() : "";
    const lng = longitude !== undefined && longitude !== null ? String(longitude).trim() : "";
    if (lat && lng) return `${lat}, ${lng}`;
    if (value && /(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/.test(value)) {
      return value;
    }
    return "";
  }, [value, latitude, longitude]);

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={`flex w-full items-center gap-2 rounded-lg border border-slate-400 bg-white px-3 py-2.5 text-left text-sm outline-none transition hover:border-indigo-400 hover:bg-indigo-50/40 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed ${
          displayLabel ? "text-slate-800 font-medium" : "text-slate-400"
        }`}
      >
        <MapPin className="h-4 w-4 shrink-0 text-indigo-600" />
        <span className="truncate">{displayLabel || "Select location on map"}</span>
      </button>
      {open && (
        <LocationMapModal
          initialValue={displayLabel}
          initialCoords={latitude && longitude ? { lat: Number(latitude), lng: Number(longitude) } : null}
          onClose={() => setOpen(false)}
          onConfirm={(locStr, addressData, coords) => {
            onChange(locStr, addressData, coords);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

function FileInput({
  file,
  remoteUrl,
  remoteName,
  attachmentId,
  hint,
  stepNo,
  description,
  labPartnerName,
  draftKey: propDraftKey,
  centerCode: propCenterCode,
  onboardingType: propOnboardingType,
  readOnly = false,
  onChange,
  onDelete,
}) {
  const inputId = useMemo(() => `file-${Math.random().toString(36).slice(2)}`, []);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const reduxDraftKey = useSelector((s) => s.ccOnboarding?.draftKey);
  const reduxOnboardingType = useSelector((s) => s.ccOnboarding?.onboardingType) || "CC";
  const reduxCenterCode = useSelector((s) => s.ccOnboarding?.facilityCode || s.ccOnboarding?.centerCode);

  const effectiveDraftKey = propDraftKey || reduxDraftKey || localStorage.getItem("cc_onboarding_draft_key");
  const effectiveOnboardingType = propOnboardingType || reduxOnboardingType || "CC";
  const effectiveCenterCode = propCenterCode || reduxCenterCode;

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (effectiveDraftKey && stepNo && description) {
      setUploading(true);
      try {
        const res = await ccOnboardingApi.uploadDraftAttachment({
          draftKey: effectiveDraftKey,
          stepNo,
          onboardingType: effectiveOnboardingType,
          userCode: currentUserCode(),
          description,
          labPartnerName: labPartnerName || undefined,
          file: selectedFile,
        });
        const attData = res?.data;
        toast.success(`${description || "File"} uploaded successfully`);
        if (onChange) {
          onChange(selectedFile, {
            attachmentId: attData?.id,
            url: attData?.viewUrl || attData?.publicUrl || attData?.fileUrl,
            fileName: attData?.fileName || selectedFile.name,
          });
        }
      } catch (err) {
        console.error("Upload draft attachment error:", err);
        toast.error(err.response?.data?.message || `Failed to upload ${description || "file"}`);
        if (onChange) onChange(selectedFile, null);
      } finally {
        setUploading(false);
      }
    } else if (effectiveCenterCode && description) {
      setUploading(true);
      try {
        const res = await ccOnboardingApi.uploadAttachment({
          centerCode: effectiveCenterCode,
          userCode: currentUserCode(),
          onboardingType: effectiveOnboardingType,
          description,
          labPartnerName: labPartnerName || undefined,
          file: selectedFile,
        });
        const attData = res?.data;
        toast.success(`${description || "File"} uploaded successfully`);
        if (onChange) {
          onChange(selectedFile, {
            attachmentId: attData?.id,
            url: attData?.viewUrl || attData?.publicUrl || attData?.fileUrl,
            fileName: attData?.fileName || selectedFile.name,
          });
        }
      } catch (err) {
        console.error("Upload attachment error:", err);
        toast.error(err.response?.data?.message || `Failed to upload ${description || "file"}`);
        if (onChange) onChange(selectedFile, null);
      } finally {
        setUploading(false);
      }
    } else {
      if (onChange) onChange(selectedFile, null);
    }
    e.target.value = "";
  };

  const handleDelete = async () => {
    if (attachmentId) {
      setDeleting(true);
      try {
        if (effectiveDraftKey) {
          await ccOnboardingApi.deleteDraftAttachment({
            draftKey: effectiveDraftKey,
            attachmentId,
            userCode: currentUserCode(),
          });
        } else if (effectiveCenterCode) {
          await ccOnboardingApi.deleteAttachment({
            attachmentId,
            userCode: currentUserCode(),
          });
        }
        toast.success(`${description || "File"} deleted successfully`);
      } catch (err) {
        console.warn("Delete attachment failed:", err);
        toast.error(err.response?.data?.message || `Failed to delete ${description || "file"}`);
      } finally {
        setDeleting(false);
      }
    }
    if (onDelete) {
      onDelete();
    } else if (onChange) {
      onChange(null, null);
    }
  };

  const hasRemoteOnly = !file && remoteUrl;

  const handleViewDoc = () => {
    const targetUrl = previewUrl || remoteUrl;
    if (!targetUrl) return;
    if (targetUrl.startsWith("blob:") || targetUrl.startsWith("data:") || targetUrl.includes("X-Goog-Signature")) {
      window.open(targetUrl, "_blank");
      return;
    }
    viewGcpDocument(targetUrl, (err) => toast.error(err));
  };

  if (uploading || deleting) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2.5 text-sm text-indigo-700">
        <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
        <span>{uploading ? `Uploading ${description || "file"}...` : `Deleting ${description || "file"}...`}</span>
      </div>
    );
  }

  if (file || hasRemoteOnly) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-400 bg-slate-50 px-3 py-2.5 text-sm">
        <Upload className="h-4 w-4 shrink-0 text-slate-400" />
        <span className="flex-1 truncate text-slate-700 font-medium">
          {file ? file.name : remoteName || description || "Uploaded document"}
        </span>
        {(previewUrl || remoteUrl) && (
          <button
            type="button"
            onClick={handleViewDoc}
            className="flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition cursor-pointer"
          >
            View
          </button>
        )}
        {!readOnly && (
          <>
            <label
              htmlFor={inputId}
              className="cursor-pointer rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              Replace
            </label>
            <button
              type="button"
              onClick={handleDelete}
              className="flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
              title="Delete upload"
            >
              <X className="h-3.5 w-3.5" /> Delete
            </button>
            <input id={inputId} type="file" className="hidden" onChange={handleFileChange} />
          </>
        )}
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-400 italic">
        No document uploaded
      </div>
    );
  }

  return (
    <label
      htmlFor={inputId}
      className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-400 bg-slate-50 px-3 py-2.5 text-sm text-slate-500 transition hover:border-indigo-400 hover:bg-indigo-50/50"
    >
      <Upload className="h-4 w-4 shrink-0 text-slate-400" />
      <span className="truncate">{hint || "Click to upload file"}</span>
      <input id={inputId} type="file" className="hidden" onChange={handleFileChange} />
    </label>
  );
}

function TopTabs({ steps, currentStep, onSelect }) {
  return (
    <div className="w-full flex justify-center bg-white border-b border-slate-200">
      <div className="flex w-full items-stretch px-4">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const active = idx === currentStep;
          return (
            <React.Fragment key={step.id}>
              <button
                type="button"
                onClick={() => onSelect(idx)}
                className={`group flex min-w-0 flex-1 items-center justify-center gap-1.5 border-b-2 px-1.5 py-3 text-sm font-medium transition ${
                  active
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-slate-400 hover:border-slate-300 hover:text-slate-600"
                }`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    active
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {idx + 1}
                </span>
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{step.label}</span>
              </button>
              {idx < steps.length - 1 && <div className="mt-6 h-px w-2 shrink-0 self-start bg-slate-300 sm:w-4" />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function LabInfoStep({ data, setField, readOnly = false }) {
  const isEclinicRole = data.userRole === "Swasthya Mitra Diagnostic";
  const isCorporateRole = data.userRole === "Cooperate Diagnostics";

  const [eclinicOptions, setEclinicOptions] = useState([]);
  const [eclinicLoading, setEclinicLoading] = useState(false);
  const [corporateOptions, setCorporateOptions] = useState([]);
  const [corporateLoading, setCorporateLoading] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [zoneLoading, setZoneLoading] = useState(false);
  const [circleOptions, setCircleOptions] = useState([]);
  const [circleLoading, setCircleLoading] = useState(false);

  const extractDropdownList = (res) => {
    if (!res) return [];
    const body = res.data ?? res;
    if (Array.isArray(body)) return body;
    if (Array.isArray(body.successList)) return body.successList;
    if (Array.isArray(body.data)) return body.data;
    if (Array.isArray(body.list)) return body.list;
    if (Array.isArray(body.result)) return body.result;
    return [];
  };

  useEffect(() => {
    let active = true;
    setStateLoading(true);
    ccOnboardingApi
      .getStateList()
      .then((res) => {
        if (active) setStateOptions(extractDropdownList(res));
      })
      .catch(() => {
        if (active) setStateOptions([]);
      })
      .finally(() => {
        if (active) setStateLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setZoneLoading(true);
    ccOnboardingApi
      .getZoneList()
      .then((res) => {
        if (active) setZoneOptions(extractDropdownList(res));
      })
      .catch(() => {
        if (active) setZoneOptions([]);
      })
      .finally(() => {
        if (active) setZoneLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const selectedStateObj = useMemo(() => {
    if (!data.state) return null;
    return (
      stateOptions.find((s) => {
        if (!s || typeof s !== "object") return false;
        return (
          s.stateCode === data.state ||
          s.code === data.state ||
          s.stateName === data.state ||
          s.name === data.state
        );
      }) || null
    );
  }, [stateOptions, data.state]);

  const stateCode = useMemo(() => {
    if (!data.state) return "";
    if (selectedStateObj) {
      return selectedStateObj.stateCode || selectedStateObj.code || selectedStateObj.id || data.state;
    }
    const STATE_CODE_MAP = {
      Odisha: "OD",
      "Tamil Nadu": "TN",
      Karnataka: "KA",
      Maharashtra: "MH",
      Delhi: "DL",
      Telangana: "TS",
      "West Bengal": "WB",
      "Andhra Pradesh": "AP",
      Bihar: "BR",
      Gujarat: "GJ",
      Rajasthan: "RJ",
      "Uttar Pradesh": "UP",
      Kerala: "KL",
      Punjab: "PB",
      Haryana: "HR",
      "Madhya Pradesh": "MP",
      Assam: "AS",
      Jharkhand: "JH",
      Chhattisgarh: "CG",
    };
    return STATE_CODE_MAP[data.state] || (typeof data.state === "string" && data.state.length <= 3 ? data.state : data.state);
  }, [selectedStateObj, data.state]);

  const currentZone = data.zone || data.zoneCode || "";

  useEffect(() => {
    if (!currentZone) {
      setCircleOptions([]);
      return undefined;
    }
    let active = true;
    setCircleLoading(true);
    ccOnboardingApi
      .getCircleList({ zone: currentZone })
      .then((res) => {
        if (active) setCircleOptions(extractDropdownList(res));
      })
      .catch(() => {
        if (active) setCircleOptions([]);
      })
      .finally(() => {
        if (active) setCircleLoading(false);
      });
    return () => {
      active = false;
    };
  }, [currentZone]);

  useEffect(() => {
    if (!isEclinicRole) return undefined;
    let active = true;
    setEclinicLoading(true);
    ccOnboardingApi
      .getEclinicDropdown()
      .then((res) => {
        if (active) setEclinicOptions(extractDropdownList(res));
      })
      .catch(() => {
        if (active) setEclinicOptions([]);
      })
      .finally(() => {
        if (active) setEclinicLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isEclinicRole]);

  useEffect(() => {
    if (!isCorporateRole) return undefined;
    let active = true;
    setCorporateLoading(true);
    ccOnboardingApi
      .getCorporateDropdown({ state: data.state })
      .then((res) => {
        if (active) setCorporateOptions(extractDropdownList(res));
      })
      .catch(() => {
        if (active) setCorporateOptions([]);
      })
      .finally(() => {
        if (active) setCorporateLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isCorporateRole, data.state]);

  return (
    <div className="flex justify-center py-6">
      <div className="w-full max-w-[40%] space-y-3">
        <Field label="User Role" required>
          <SearchableSelect
            value={data.userRole}
            options={USER_ROLE_OPTIONS}
            disabled={readOnly}
            placeholder="Select User Role"
            searchPlaceholder="Search user role…"
            getLabel={(r) => (typeof r === "string" ? r : r.name || r.label || r.value || "")}
            getKey={(r) => (typeof r === "string" ? r : r.value || r.name || "")}
            onChange={(val) => {
              setField("userRole", val);
              setField("eclinicCode", "");
              setField("eclinicName", "");
              setField("corporateCode", "");
              setField("corporateName", "");
            }}
          />
        </Field>

        {isEclinicRole && (
          <Field label="ECLINIC Name" required>
            <SearchableSelect
              value={data.eclinicCode}
              loading={eclinicLoading}
              options={eclinicOptions}
              stateFilter={data.state}
              disabled={readOnly}
              placeholder="Search & Select eClinic"
              searchPlaceholder="Type to search by name, code or city…"
              getLabel={(o) => o.hospitalName || o.eclinicName || o.name || ""}
              getSubtitle={(o) => [o.hospitalCode || o.eclinicCode, o.city].filter(Boolean).join(" • ")}
              onChange={(val, opt) => {
                setField("eclinicCode", val);
                setField("eclinicName", opt?.hospitalName || opt?.eclinicName || opt?.name || "");
                setField("mobilabId", opt?.mobilabId ?? "");
                if (opt?.state && !data.state) setField("state", opt.state);
                if (opt?.city && !data.city) setField("city", opt.city);
              }}
            />
          </Field>
        )}

        {isCorporateRole && (
          <Field label="Corporate" required>
            <SearchableSelect
              value={data.corporateCode}
              loading={corporateLoading}
              options={corporateOptions}
              disabled={readOnly}
              placeholder="Select Corporate"
              searchPlaceholder="Type to search corporate…"
              getLabel={(o) => o.corporateName || o.name || ""}
              getKey={(o) => o.corporateCode || o.code || o.id}
              onChange={(val, opt) => {
                setField("corporateCode", val);
                setField("corporateName", opt?.corporateName || opt?.name || "");
              }}
            />
          </Field>
        )}

        <Field label="CC Name" required>
          <TextInput
            placeholder="e.g. Sunrise Diagnostics"
            value={data.labName}
            disabled={readOnly}
            onChange={(e) => setField("labName", e.target.value)}
          />
        </Field>

        <Field label="Location">
          <LocationPicker
            value={data.location}
            latitude={data.latitude}
            longitude={data.longitude}
            disabled={readOnly}
            onChange={(locStr, addressData, coords) => {
              const latStr = coords?.lat ? String(coords.lat).trim() : data.latitude ? String(data.latitude).trim() : "";
              const lngStr = coords?.lng ? String(coords.lng).trim() : data.longitude ? String(data.longitude).trim() : "";
              const formattedCoords = latStr && lngStr ? `${latStr}, ${lngStr}` : locStr;
              setField("location", formattedCoords);
              if (latStr) setField("latitude", latStr);
              if (lngStr) setField("longitude", lngStr);
              if (addressData?.area) setField("area", addressData.area);
              if (addressData?.address) setField("address", addressData.address);
              if (addressData?.city && !data.city) setField("city", addressData.city);
              if (addressData?.state && !data.state) setField("state", addressData.state);
              if (addressData?.pinCode && !data.pinCode) setField("pinCode", addressData.pinCode);
            }}
          />
        </Field>

        <Field label="Address" required>
          <textarea
            placeholder="Full address"
            rows={3}
            value={data.address}
            disabled={readOnly}
            onChange={(e) => setField("address", e.target.value)}
            className={`${inputCls} resize-none`}
          />
        </Field>

        <Field label="City" required>
          <TextInput
            placeholder="City"
            value={data.city}
            disabled={readOnly}
            onChange={(e) => setField("city", e.target.value)}
          />
        </Field>

        <Field label="State" required>
          <SearchableSelect
            value={data.state}
            loading={stateLoading}
            options={stateOptions.length ? stateOptions : ["Tamil Nadu", "Karnataka", "Maharashtra", "Delhi", "Odisha", "Telangana"]}
            disabled={readOnly}
            placeholder="Select State"
            searchPlaceholder="Search state…"
            onChange={(val, opt) => setField("state", typeof opt === "object" ? opt?.stateName || opt?.name || val : val)}
          />
        </Field>

        <Field label="Zone">
          <SearchableSelect
            value={currentZone}
            loading={zoneLoading}
            options={zoneOptions}
            disabled={readOnly}
            placeholder="Select Zone"
            searchPlaceholder="Search zone…"
            getLabel={(z) => (typeof z === "string" ? z : z?.name || z?.description || z?.zoneName || z?.code || "")}
            getKey={(z) => (typeof z === "string" ? z : z?.code || z?.value || z?.id || z?.name || "")}
            onChange={(val) => {
              setField("zone", val);
              setField("zoneCode", val);
              setField("circle", "");
              setField("circleCode", "");
            }}
          />
        </Field>

        <Field label="Circle">
          <SearchableSelect
            value={data.circle || data.circleCode}
            loading={circleLoading}
            options={circleOptions}
            disabled={readOnly || !currentZone}
            placeholder={currentZone ? "Select Circle" : "Select Zone first"}
            searchPlaceholder="Search circle…"
            getLabel={(c) => (typeof c === "string" ? c : c?.name || c?.circleName || c?.description || c?.code || "")}
            getKey={(c) => (typeof c === "string" ? c : c?.code || c?.circleCode || c?.value || c?.id || c?.name || "")}
            onChange={(val) => {
              setField("circle", val);
              setField("circleCode", val);
            }}
          />
        </Field>

        <Field label="Pin Code" required>
          <TextInput
            placeholder="751001"
            maxLength={6}
            value={data.pinCode}
            disabled={readOnly}
            onChange={(e) => setField("pinCode", e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
        </Field>

        <Field label="Diagnostic Type" required>
          <div className="flex h-[42px] items-center gap-4">
            {DIAGNOSTIC_TYPE_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                <input
                  type="radio"
                  name="diagnosticType"
                  disabled={readOnly}
                  checked={data.diagnosticType === opt.value}
                  onChange={() => setField("diagnosticType", opt.value)}
                  className="h-4 w-4 accent-indigo-600"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Mobilab Availability">
          <SearchableSelect
            value={data.mobilabAvailabity || data.mobilabAvailability || ""}
            options={MOBILAB_AVAILABILITY_OPTIONS}
            disabled={readOnly}
            placeholder="Mobilab Availability"
            searchPlaceholder="Search Mobilab Availability…"
            getLabel={(o) => (typeof o === "string" ? o : o.label || o.value)}
            getKey={(o) => (typeof o === "string" ? o : o.value)}
            onChange={(val) => {
              setField("mobilabAvailabity", val);
              setField("mobilabAvailability", val);
            }}
          />
        </Field>

        <Field label="Send reports to Patients ?">
          <div className="flex h-[42px] items-center gap-4">
            {SEND_REPORT_TO_PATIENT_OPTIONS.map((opt) => (
              <label key={opt.label} className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                <input
                  type="radio"
                  name="sendReportToPatient"
                  disabled={readOnly}
                  checked={
                    data.sendReportToPatient === opt.value ||
                    (opt.value === 0 && (data.sendReportToPatient === 0 || data.sendReportToPatient === "0" || String(data.sendReportToPatient).toUpperCase() === "NO")) ||
                    (opt.value === 1 && (data.sendReportToPatient === 1 || data.sendReportToPatient === "1" || String(data.sendReportToPatient).toUpperCase() === "YES"))
                  }
                  onChange={() => setField("sendReportToPatient", opt.value)}
                  className="h-4 w-4 accent-indigo-600"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Automapping">
          <div className="flex h-[42px] items-center gap-4">
            {YES_NO_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                <input
                  type="radio"
                  name="automapping"
                  disabled={readOnly}
                  checked={data.automapping === opt.value}
                  onChange={() => setField("automapping", opt.value)}
                  className="h-4 w-4 accent-indigo-600"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Agreement Date">
          <DateInput
            value={data.agreementDate}
            disabled={readOnly}
            onChange={(val) => setField("agreementDate", val)}
          />
        </Field>

        <Field label="Termination Notice Period">
          <TextInput
            placeholder="e.g. 30 days"
            value={data.terminationNoticePeriod}
            disabled={readOnly}
            onChange={(e) => setField("terminationNoticePeriod", e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}

function ContactsStep({ contacts, dispatch, readOnly = false }) {
  const sourceOptions = ["Advertisement", "Campaign", "Others", "Referral", "Social Media", "Website"];
  const qualificationOptions = ["10th Pass", "12th Pass", "Graduate", "Post Graduate"];

  return (
    <div className="flex justify-center py-6">
      <div className="w-full max-w-[40%] space-y-3">
        {contacts.map((c, idx) => (
          <div key={c.id} className="relative rounded-xl border border-slate-300 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="inline-block rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-600">
                Contact #{idx + 1}
              </span>
              {contacts.length > 1 && !readOnly && (
                <button
                  type="button"
                  onClick={() => dispatch({ type: "REMOVE_CONTACT", payload: c.id })}
                  className="text-slate-400 transition hover:text-rose-500"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="space-y-3">
              <Field label="Gender">
                <SearchableSelect
                  value={c.gender}
                  options={["Male", "Female", "Other"]}
                  disabled={readOnly}
                  placeholder="Select Gender"
                  searchPlaceholder="Search gender…"
                  onChange={(val) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "gender", value: val } })}
                />
              </Field>

              <Field label="Salutation">
                <SearchableSelect
                  value={c.salutation}
                  options={getSalutationOptions()}
                  disabled={readOnly}
                  placeholder="Select Salutation"
                  searchPlaceholder="Search salutation…"
                  onChange={(val) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "salutation", value: val } })}
                />
              </Field>

              <Field label="First Name" required>
                <TextInput
                  placeholder="First name"
                  value={c.firstName}
                  disabled={readOnly}
                  onChange={(e) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "firstName", value: e.target.value } })}
                />
              </Field>

              <Field label="Last Name" required>
                <TextInput
                  placeholder="Last name"
                  value={c.lastName}
                  disabled={readOnly}
                  onChange={(e) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "lastName", value: e.target.value } })}
                />
              </Field>

              <Field label="Designation">
                <TextInput
                  placeholder="e.g. Manager"
                  value={c.designation}
                  disabled={readOnly}
                  onChange={(e) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "designation", value: e.target.value } })}
                />
              </Field>

              <Field label="Mobile Number" required>
                <TextInput
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  value={c.mobileNumber}
                  disabled={readOnly}
                  onChange={(e) =>
                    dispatch({
                      type: "UPDATE_CONTACT",
                      payload: {
                        id: c.id,
                        field: "mobileNumber",
                        value: e.target.value.replace(/\D/g, "").slice(0, 10),
                      },
                    })
                  }
                />
              </Field>

              <Field label="Email ID" required>
                <TextInput
                  placeholder="contact@lab.com"
                  value={c.email}
                  disabled={readOnly}
                  onChange={(e) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "email", value: e.target.value } })}
                />
              </Field>

              <Field label="Date of Birth">
                <DateInput
                  value={c.dob}
                  disabled={readOnly}
                  maxDate={new Date().toISOString().split("T")[0]}
                  placeholder="Select Date of Birth"
                  onChange={(val) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "dob", value: val } })}
                />
              </Field>

              <Field label="Source">
                <SearchableSelect
                  value={c.source}
                  options={sourceOptions}
                  disabled={readOnly}
                  placeholder="Select Source"
                  searchPlaceholder="Search source…"
                  onChange={(val) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "source", value: val } })}
                />
              </Field>

              <Field label="Qualification">
                <SearchableSelect
                  value={c.qualification}
                  options={qualificationOptions}
                  disabled={readOnly}
                  placeholder="Select Qualification"
                  searchPlaceholder="Search qualification…"
                  onChange={(val) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "qualification", value: val } })}
                />
              </Field>

              <Field label="PAN Number" required>
                <TextInput
                  placeholder="ABCDE1234F"
                  value={c.panNumber}
                  disabled={readOnly}
                  onChange={(e) => dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "panNumber", value: e.target.value } })}
                />
              </Field>

              <Field label="SM Photo" required>
                <FileInput
                  file={c.smPhoto}
                  remoteUrl={c.remoteSmPhoto}
                  remoteName={c.remoteSmPhotoName || "SM Photo"}
                  attachmentId={c.attachmentId}
                  stepNo={2}
                  description={ATTACHMENT_SLOTS.SM_PHOTO}
                  readOnly={readOnly}
                  hint="Upload photo of the Swasthya Mitra"
                  onChange={(file, extra) => {
                    dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "smPhoto", value: file } });
                    if (extra?.url) {
                      dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "remoteSmPhoto", value: extra.url } });
                    }
                    if (extra?.attachmentId) {
                      dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "attachmentId", value: extra.attachmentId } });
                    }
                    if (file === null) {
                      dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "remoteSmPhoto", value: null } });
                      dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "attachmentId", value: null } });
                    }
                  }}
                  onDelete={() => {
                    dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "smPhoto", value: null } });
                    dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "remoteSmPhoto", value: null } });
                    dispatch({ type: "UPDATE_CONTACT", payload: { id: c.id, field: "attachmentId", value: null } });
                  }}
                />
              </Field>
            </div>
          </div>
        ))}
        {!readOnly && (
          <button
            type="button"
            onClick={() => dispatch({ type: "ADD_CONTACT" })}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-indigo-400 py-2.5 text-sm font-medium text-indigo-600 transition hover:bg-indigo-50"
          >
            <Plus className="h-4 w-4" /> Add Another Contact Person
          </button>
        )}
      </div>
    </div>
  );
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_SHORT = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

function TestTimingsStep({ data, dispatch, readOnly = false }) {
  const timings = data.timings || [];

  const getAssignedDays = (excludeId = null) => {
    const assigned = new Set();
    timings.forEach((t) => {
      if (excludeId && t.id === excludeId) return;
      const days = Array.isArray(t.days) && t.days.length ? t.days : t.day ? [t.day] : [];
      days.forEach((d) => assigned.add(d));
    });
    return assigned;
  };

  const allAssignedDays = getAssignedDays();
  const allDaysCovered = DAYS.every((d) => allAssignedDays.has(d));
  const hasEmptySlot = timings.some((t) => {
    const dList = Array.isArray(t.days) && t.days.length ? t.days : t.day ? [t.day] : [];
    return dList.length === 0;
  });
  const canAddSlot = !readOnly && !allDaysCovered && !hasEmptySlot;

  const handleToggleDay = (timingId, day) => {
    if (readOnly) return;
    const nextTimings = timings.map((t) => {
      const currentDays = Array.isArray(t.days) && t.days.length ? t.days : t.day ? [t.day] : [];
      if (t.id === timingId) {
        const nextDays = currentDays.includes(day)
          ? currentDays.filter((d) => d !== day)
          : [...currentDays, day];
        return { ...t, days: nextDays };
      } else {
        // Automatically unselect day from previous slot if selected there
        const nextDays = currentDays.filter((d) => d !== day);
        return { ...t, days: nextDays };
      }
    });

    dispatch({
      type: "SET_TIMINGS",
      payload: nextTimings,
    });
    dispatch({ type: "CLEAR_ERROR" });
  };

  const handleSetPreset = (timingId, presetType) => {
    if (readOnly) return;
    let targetDays = [];
    if (presetType === "ALL") {
      targetDays = [...DAYS];
    } else if (presetType === "WEEKDAYS") {
      targetDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    } else if (presetType === "WEEKEND") {
      targetDays = ["Saturday", "Sunday"];
    } else if (presetType === "CLEAR") {
      targetDays = [];
    }

    const nextTimings = timings.map((t) => {
      const currentDays = Array.isArray(t.days) && t.days.length ? t.days : t.day ? [t.day] : [];
      if (t.id === timingId) {
        return { ...t, days: targetDays };
      } else {
        const nextDays = currentDays.filter((d) => !targetDays.includes(d));
        return { ...t, days: nextDays };
      }
    });

    dispatch({
      type: "SET_TIMINGS",
      payload: nextTimings,
    });
    dispatch({ type: "CLEAR_ERROR" });
  };

  return (
    <div className="flex justify-center py-6">
      <div className="w-full max-w-[40%] space-y-4">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">CC Timings</h3>
            <span className="text-xs text-slate-500">
              {allAssignedDays.size} of 7 days configured
            </span>
          </div>

          <div className="space-y-3">
            {timings.map((t, idx) => {
              const currentDays = Array.isArray(t.days) && t.days.length ? t.days : t.day ? [t.day] : [];
              const otherAssigned = getAssignedDays(t.id);

              return (
                <div key={t.id} className="space-y-3 rounded-xl border border-slate-300 bg-slate-50 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                      Timing Slot #{idx + 1}
                    </span>
                    {timings.length > 1 && !readOnly && (
                      <button
                        type="button"
                        onClick={() => dispatch({ type: "REMOVE_TIMING", payload: t.id })}
                        className="flex items-center gap-1 text-xs font-medium text-rose-500 hover:text-rose-600"
                      >
                        <X className="h-3.5 w-3.5" /> Remove
                      </button>
                    )}
                  </div>

                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                        Select Days <span className="text-rose-500">*</span>
                      </label>
                      {!readOnly && (
                        <div className="flex items-center gap-1 text-[11px] text-indigo-600">
                          <button
                            type="button"
                            onClick={() => handleSetPreset(t.id, "WEEKDAYS")}
                            className="hover:underline"
                          >
                            Mon–Fri
                          </button>
                          <span>•</span>
                          <button
                            type="button"
                            onClick={() => handleSetPreset(t.id, "WEEKEND")}
                            className="hover:underline"
                          >
                            Sat–Sun
                          </button>
                          <span>•</span>
                          <button
                            type="button"
                            onClick={() => handleSetPreset(t.id, "ALL")}
                            className="hover:underline"
                          >
                            All Days
                          </button>
                          <span>•</span>
                          <button
                            type="button"
                            onClick={() => handleSetPreset(t.id, "CLEAR")}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {DAYS.map((day) => {
                        const isSelected = currentDays.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            disabled={readOnly}
                            onClick={() => handleToggleDay(t.id, day)}
                            className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${
                              isSelected
                                ? "bg-indigo-600 font-semibold text-white shadow-sm hover:bg-indigo-700"
                                : "border border-slate-300 bg-white text-slate-700 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50"
                            }`}
                            title={day}
                          >
                            {DAY_SHORT[day]}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Opens At" required>
                      <TextInput
                        type="time"
                        value={t.opensAt}
                        disabled={readOnly}
                        onChange={(e) =>
                          dispatch({
                            type: "UPDATE_TIMING",
                            payload: { id: t.id, field: "opensAt", value: e.target.value },
                          })
                        }
                      />
                    </Field>
                    <Field label="Closes At" required>
                      <TextInput
                        type="time"
                        value={t.closesAt}
                        disabled={readOnly}
                        onChange={(e) =>
                          dispatch({
                            type: "UPDATE_TIMING",
                            payload: { id: t.id, field: "closesAt", value: e.target.value },
                          })
                        }
                      />
                    </Field>
                  </div>
                </div>
              );
            })}
          </div>

          {!readOnly && (
            <button
              type="button"
              onClick={() => {
                if (!canAddSlot) return;
                dispatch({ type: "ADD_TIMING" });
                dispatch({ type: "CLEAR_ERROR" });
              }}
              disabled={!canAddSlot}
              className={`mt-3 inline-flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm font-medium transition ${
                !canAddSlot
                  ? "cursor-not-allowed border-slate-300 bg-slate-50 text-slate-400"
                  : "border-indigo-400 text-indigo-600 hover:bg-indigo-50"
              }`}
              title={
                hasEmptySlot
                  ? "Please select days in each slot before adding another"
                  : allDaysCovered
                  ? "All 7 days configured"
                  : "Add another timing slot"
              }
            >
              <Plus className="h-4 w-4" /> Add Another Timing Slot{" "}
              {allDaysCovered
                ? "(All 7 days configured)"
                : hasEmptySlot
                ? "(Select days above first)"
                : ""}
            </button>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-900">Attachments</h3>
          <div className="space-y-2">
            {(data.attachments || []).map((a) => (
              <div key={a.id} className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                <Field label="Lab Partner Name">
                  <TextInput
                    placeholder="Lab Partner Name"
                    value={a.partnerName || ""}
                    disabled={readOnly}
                    onChange={(e) =>
                      dispatch({
                        type: "UPDATE_ATTACHMENT",
                        payload: { id: a.id, field: "partnerName", value: e.target.value },
                      })
                    }
                  />
                </Field>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <FileInput
                      file={a.file}
                      remoteUrl={a.remoteFileUrl}
                      remoteName={a.remoteFileName || a.description || "Test Attachment"}
                      attachmentId={a.attachmentId}
                      stepNo={3}
                      labPartnerName={a.partnerName || data.labPartnerName}
                      description={a.description || (a.partnerName ? `Test Attachment - ${a.partnerName}` : "Test Rate Card")}
                      readOnly={readOnly}
                      hint="Click to upload file"
                      onChange={(file, extra) => {
                        dispatch({ type: "SET_ATTACHMENT_FILE", payload: { id: a.id, file } });
                        if (extra?.url) {
                          dispatch({ type: "UPDATE_ATTACHMENT", payload: { id: a.id, field: "remoteFileUrl", value: extra.url } });
                        }
                        if (extra?.fileName) {
                          dispatch({ type: "UPDATE_ATTACHMENT", payload: { id: a.id, field: "remoteFileName", value: extra.fileName } });
                        }
                        if (extra?.attachmentId) {
                          dispatch({ type: "UPDATE_ATTACHMENT", payload: { id: a.id, field: "attachmentId", value: extra.attachmentId } });
                        }
                        if (file === null) {
                          dispatch({ type: "UPDATE_ATTACHMENT", payload: { id: a.id, field: "remoteFileUrl", value: null } });
                          dispatch({ type: "UPDATE_ATTACHMENT", payload: { id: a.id, field: "attachmentId", value: null } });
                        }
                      }}
                      onDelete={() => {
                        dispatch({ type: "REMOVE_ATTACHMENT", payload: a.id });
                      }}
                    />
                  </div>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => dispatch({ type: "REMOVE_ATTACHMENT", payload: a.id })}
                      className="text-slate-400 hover:text-rose-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!data.attachments?.length && <p className="text-xs text-slate-400">No attachments added yet.</p>}
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => dispatch({ type: "ADD_ATTACHMENT" })}
              className="mt-3 inline-flex items-center gap-2 rounded-lg border border-dashed border-indigo-400 px-3 py-2 text-sm font-medium text-indigo-600 transition hover:bg-indigo-50"
            >
              <Plus className="h-4 w-4" /> Add Another Attachment
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LabGroupSection({ title, required = false, group, rows = [], dispatch, labOptions, loading, readOnly }) {
  const isRadiology = group?.toLowerCase().includes("radiology");
  const rowList = Array.isArray(rows) ? rows : [];
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {title} {required && <span className="text-rose-500">*</span>}
        </h4>
        {!readOnly && (
          <button
            type="button"
            onClick={() => dispatch({ type: "ADD_LAB_ROW", payload: { group } })}
            className="flex items-center gap-1 rounded-lg bg-emerald-500 px-2 py-1 text-xs font-semibold text-white transition hover:bg-emerald-600"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        )}
      </div>
      <div className="space-y-2">
        {rowList.map((row) => (
          <div key={row.id} className="space-y-2">
            <Field label={isRadiology ? "Radiology Lab" : "Pathology Lab"} required={required}>
              <SearchableSelect
                value={row.labCode}
                loading={loading}
                options={labOptions}
                disabled={readOnly}
                placeholder={isRadiology ? "Select Radiology Lab" : "Select Pathology Lab"}
                searchPlaceholder="Search lab by name or code…"
                getLabel={(o) => (typeof o === "string" ? o : o.labName || o.partnerName || o.name || "")}
                getKey={(o) => (typeof o === "string" ? o : String(o.labCode ?? o.partnerCode ?? o.code ?? o.id ?? ""))}
                onChange={(val, opt) => {
                  dispatch({ type: "UPDATE_LAB_ROW", payload: { group, id: row.id, field: "labCode", value: val } });
                  dispatch({
                    type: "UPDATE_LAB_ROW",
                    payload: { group, id: row.id, field: "labName", value: typeof opt === "object" ? opt?.labName || opt?.partnerName || opt?.name || "" : "" },
                  });
                }}
              />
            </Field>
            <Field label="Percentage">
              <TextInput
                type="number"
                placeholder="Percentage"
                value={row.commission}
                disabled={readOnly}
                onChange={(e) => dispatch({ type: "UPDATE_LAB_ROW", payload: { group, id: row.id, field: "commission", value: e.target.value } })}
              />
            </Field>
            {!readOnly && (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => dispatch({ type: "REMOVE_LAB_ROW", payload: { group, id: row.id } })}
                  className="flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs font-medium text-rose-500 transition hover:bg-rose-50"
                >
                  <X className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            )}
          </div>
        ))}
        {!rows.length && <p className="text-xs text-slate-400">No labs added yet.</p>}
      </div>
    </div>
  );
}

const PAYMENT_PERIOD_OPTIONS = [
  { label: "7 days", value: "weekly" },
  { label: "15 Days", value: "biweekly" },
  { label: "30 Days", value: "monthly" },
];

const RECONCILIATION_OPTIONS = [
  { label: "Automatic", value: "auto" },
  { label: "Manual", value: "manual" },
];

function PaymentStep({ data, setField, diagnosticType, automapping, pinCode, dispatch, readOnly = false }) {
  const isAutoMapping = Number(automapping) === 1 || automapping === "1" || automapping === true;
  const needsPathology = !isAutoMapping && (diagnosticType === 1 || diagnosticType === 3);
  const needsRadiology = !isAutoMapping && (diagnosticType === 2 || diagnosticType === 3);

  const [pathologyLabOptions, setPathologyLabOptions] = useState([]);
  const [radiologyLabOptions, setRadiologyLabOptions] = useState([]);
  const [preferredPathologyOptions, setPreferredPathologyOptions] = useState([]);
  const [preferredRadiologyOptions, setPreferredRadiologyOptions] = useState([]);
  const [pathologyLoading, setPathologyLoading] = useState(false);
  const [radiologyLoading, setRadiologyLoading] = useState(false);
  const [preferredPathologyLoading, setPreferredPathologyLoading] = useState(false);
  const [preferredRadiologyLoading, setPreferredRadiologyLoading] = useState(false);

  useEffect(() => {
    if (!needsPathology) return undefined;
    let active = true;
    setPathologyLoading(true);
    ccOnboardingApi
      .getLabListByType("PATHOLOGY")
      .then((res) => {
        if (active) setPathologyLabOptions(res?.data?.data || res?.data || []);
      })
      .catch(() => {
        if (active) setPathologyLabOptions([]);
      })
      .finally(() => {
        if (active) setPathologyLoading(false);
      });
    setPreferredPathologyLoading(true);
    ccOnboardingApi
      .getNearLabList({ pinCode, diagnosticType: "PATHOLOGY" })
      .then((res) => {
        if (active) setPreferredPathologyOptions(res?.data?.data || res?.data || []);
      })
      .catch(() => {
        if (active) setPreferredPathologyOptions([]);
      })
      .finally(() => {
        if (active) setPreferredPathologyLoading(false);
      });
    return () => {
      active = false;
    };
  }, [needsPathology, pinCode]);

  useEffect(() => {
    if (!needsRadiology) return undefined;
    let active = true;
    setRadiologyLoading(true);
    ccOnboardingApi
      .getLabListByType("RADIOLOGY")
      .then((res) => {
        if (active) setRadiologyLabOptions(res?.data?.data || res?.data || []);
      })
      .catch(() => {
        if (active) setRadiologyLabOptions([]);
      })
      .finally(() => {
        if (active) setRadiologyLoading(false);
      });
    setPreferredRadiologyLoading(true);
    ccOnboardingApi
      .getNearLabList({ pinCode, diagnosticType: "RADIOLOGY" })
      .then((res) => {
        if (active) setPreferredRadiologyOptions(res?.data?.data || res?.data || []);
      })
      .catch(() => {
        if (active) setPreferredRadiologyOptions([]);
      })
      .finally(() => {
        if (active) setPreferredRadiologyLoading(false);
      });
    return () => {
      active = false;
    };
  }, [needsRadiology, pinCode]);

  return (
    <div className="flex justify-center py-6">
      <div className="w-full max-w-[40%] space-y-3">
        <Field label="Payment Period" required>
          <SearchableSelect
            value={data.paymentPeriod}
            options={PAYMENT_PERIOD_OPTIONS}
            disabled={readOnly}
            placeholder="Select Payment Period"
            searchPlaceholder="Search period…"
            getLabel={(o) => (typeof o === "string" ? o : o.label || o.value || "")}
            getKey={(o) => (typeof o === "string" ? o : o.value || o.label || "")}
            onChange={(val) => setField("paymentPeriod", val)}
          />
        </Field>
        <Field label="Payment Reconciliation">
          <SearchableSelect
            value={data.paymentReconciliation}
            options={RECONCILIATION_OPTIONS}
            disabled={readOnly}
            placeholder="Select Reconciliation"
            searchPlaceholder="Search reconciliation…"
            getLabel={(o) => (typeof o === "string" ? o : o.label || o.value || "")}
            getKey={(o) => (typeof o === "string" ? o : o.value || o.label || "")}
            onChange={(val) => setField("paymentReconciliation", val)}
          />
        </Field>
        <Field label="Is B2B2C ?">
          <div className="flex h-[42px] items-center gap-4">
            {YES_NO_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                <input
                  type="radio"
                  name="isCureBayLabCenter"
                  disabled={readOnly}
                  checked={data.isCureBayLabCenter === opt.value}
                  onChange={() => setField("isCureBayLabCenter", opt.value)}
                  className="h-4 w-4 accent-indigo-600"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </Field>

        {!isAutoMapping && !diagnosticType && (
          <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
            Select a Diagnostic Type on the Collection Center tab to configure lab groups.
          </p>
        )}

        {!isAutoMapping && needsRadiology && (
          <>
            <LabGroupSection
              title="Radiology Labs Group"
              group="radiologyLabs"
              rows={data.radiologyLabs}
              dispatch={dispatch}
              labOptions={radiologyLabOptions}
              loading={radiologyLoading}
              readOnly={readOnly}
            />
            <LabGroupSection
              title="Preferred Radiology Labs"
              required
              group="preferredRadiologyLabs"
              rows={data.preferredRadiologyLabs}
              dispatch={dispatch}
              labOptions={preferredRadiologyOptions}
              loading={preferredRadiologyLoading}
              readOnly={readOnly}
            />
          </>
        )}

        {!isAutoMapping && needsPathology && (
          <>
            <LabGroupSection
              title="Pathology Labs Group"
              group="pathologyLabs"
              rows={data.pathologyLabs}
              dispatch={dispatch}
              labOptions={pathologyLabOptions}
              loading={pathologyLoading}
              readOnly={readOnly}
            />
            <LabGroupSection
              title="Preferred Pathology Labs"
              required
              group="preferredPathologyLabs"
              rows={data.preferredPathologyLabs}
              dispatch={dispatch}
              labOptions={preferredPathologyOptions}
              loading={preferredPathologyLoading}
              readOnly={readOnly}
            />
          </>
        )}
      </div>
    </div>
  );
}

function ComplianceStep({ data, setField, readOnly = false }) {
  return (
    <div className="flex justify-center py-6">
      <div className="w-full max-w-[40%] space-y-3">
        <Field label="GST Registration Number">
          <TextInput
            placeholder="22AAAAA0000A1Z5"
            value={data.gstNumber}
            disabled={readOnly}
            onChange={(e) => setField("gstNumber", e.target.value)}
          />
        </Field>
        <Field label="NABL Number">
          <TextInput
            placeholder="MC-1234"
            value={data.nablNumber}
            disabled={readOnly}
            onChange={(e) => setField("nablNumber", e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}

function BankStep({ data, setField, readOnly = false }) {
  return (
    <div className="flex justify-center py-6">
      <div className="w-full max-w-[40%] space-y-3">
        <Field label="Bank Name">
          <TextInput
            placeholder="e.g. State Bank of India"
            value={data.bankName}
            disabled={readOnly}
            onChange={(e) => setField("bankName", e.target.value)}
          />
        </Field>
        <Field label="Account Holder Name">
          <TextInput
            placeholder="Name as per bank records"
            value={data.accountHolderName}
            disabled={readOnly}
            onChange={(e) => setField("accountHolderName", e.target.value)}
          />
        </Field>
        <Field label="Account Number">
          <TextInput
            placeholder="e.g. 12345678901234"
            value={data.accountNumber}
            disabled={readOnly}
            onChange={(e) => setField("accountNumber", e.target.value)}
          />
        </Field>
        <Field label="IFSC Code">
          <TextInput
            placeholder="SBIN0001234"
            value={data.ifscCode}
            disabled={readOnly}
            onChange={(e) => setField("ifscCode", e.target.value)}
          />
        </Field>
        <Field label="Branch Location">
          <TextInput
            placeholder="Branch city / address"
            value={data.branchLocation}
            disabled={readOnly}
            onChange={(e) => setField("branchLocation", e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}

function DocumentsStep({ data, setField, readOnly = false }) {
  const remote = data.remote || {};
  const attachmentIdsMap = data.attachmentIdsMap || {};

  const handleDocChange = (fieldKey, desc, file, extra) => {
    setField(fieldKey, file);
    const updatedRemote = { ...(data.remote || {}) };
    const updatedIds = { ...(data.attachmentIdsMap || {}) };

    if (extra?.url) {
      updatedRemote[fieldKey] = extra.url;
      setField("remote", updatedRemote);
    }
    if (extra?.attachmentId) {
      updatedIds[fieldKey] = extra.attachmentId;
      setField("attachmentIdsMap", updatedIds);
      setField("attachmentIds", Object.values(updatedIds).filter(Boolean));
    }
    if (file === null) {
      delete updatedRemote[fieldKey];
      delete updatedIds[fieldKey];
      setField("remote", updatedRemote);
      setField("attachmentIdsMap", updatedIds);
      setField("attachmentIds", Object.values(updatedIds).filter(Boolean));
    }
  };

  const handleDocDelete = (fieldKey) => {
    setField(fieldKey, null);
    const updatedRemote = { ...(data.remote || {}) };
    const updatedIds = { ...(data.attachmentIdsMap || {}) };
    delete updatedRemote[fieldKey];
    delete updatedIds[fieldKey];
    setField("remote", updatedRemote);
    setField("attachmentIdsMap", updatedIds);
    setField("attachmentIds", Object.values(updatedIds).filter(Boolean));
  };

  return (
    <div className="flex justify-center py-6">
      <div className="w-full max-w-[40%] space-y-3">
        <Field label="Agreement Copy">
          <FileInput
            file={data.contractCopy}
            remoteUrl={remote.contractCopy || data.agreementCopy}
            attachmentId={attachmentIdsMap.contractCopy}
            stepNo={7}
            description={ATTACHMENT_SLOTS.AGREEMENT_COPY}
            readOnly={readOnly}
            hint="Upload Agreement Copy"
            onChange={(f, extra) => handleDocChange("contractCopy", ATTACHMENT_SLOTS.AGREEMENT_COPY, f, extra)}
            onDelete={() => handleDocDelete("contractCopy")}
          />
        </Field>
        <Field label="Cancelled Cheque / Bank Details">
          <FileInput
            file={data.bankProof}
            remoteUrl={remote.bankProof || data.cancelledCheque}
            attachmentId={attachmentIdsMap.bankProof}
            stepNo={7}
            description={ATTACHMENT_SLOTS.CANCELLED_CHEQUE}
            readOnly={readOnly}
            hint="Upload Cancelled Cheque / Bank Details"
            onChange={(f, extra) => handleDocChange("bankProof", ATTACHMENT_SLOTS.CANCELLED_CHEQUE, f, extra)}
            onDelete={() => handleDocDelete("bankProof")}
          />
        </Field>
        <Field label="CC Logo">
          <FileInput
            file={data.labLogo}
            remoteUrl={remote.labLogo || data.ccLogo}
            attachmentId={attachmentIdsMap.labLogo}
            stepNo={7}
            description={ATTACHMENT_SLOTS.CC_LOGO}
            readOnly={readOnly}
            hint="Upload CC Logo"
            onChange={(f, extra) => handleDocChange("labLogo", ATTACHMENT_SLOTS.CC_LOGO, f, extra)}
            onDelete={() => handleDocDelete("labLogo")}
          />
        </Field>
        <Field label="Aadhar KYC Document">
          <FileInput
            file={data.kycDocument}
            remoteUrl={remote.kycDocument || data.aadharKyc}
            attachmentId={attachmentIdsMap.kycDocument}
            stepNo={7}
            description={ATTACHMENT_SLOTS.AADHAAR_KYC}
            readOnly={readOnly}
            hint="Upload KYC Document"
            onChange={(f, extra) => handleDocChange("kycDocument", ATTACHMENT_SLOTS.AADHAAR_KYC, f, extra)}
            onDelete={() => handleDocDelete("kycDocument")}
          />
        </Field>
        <Field label="PAN Card Document">
          <FileInput
            file={data.panCard}
            remoteUrl={remote.panCard || data.panCardPhoto}
            attachmentId={attachmentIdsMap.panCard}
            stepNo={7}
            description={ATTACHMENT_SLOTS.PAN_CARD}
            readOnly={readOnly}
            hint="Upload PAN Card Document"
            onChange={(f, extra) => handleDocChange("panCard", ATTACHMENT_SLOTS.PAN_CARD, f, extra)}
            onDelete={() => handleDocDelete("panCard")}
          />
        </Field>
        <Field label="GST Certificate">
          <FileInput
            file={data.gstCertificate}
            remoteUrl={remote.gstCertificate}
            attachmentId={attachmentIdsMap.gstCertificate}
            stepNo={7}
            description={ATTACHMENT_SLOTS.GST_CERTIFICATE}
            readOnly={readOnly}
            hint="Upload GST Certificate"
            onChange={(f, extra) => handleDocChange("gstCertificate", ATTACHMENT_SLOTS.GST_CERTIFICATE, f, extra)}
            onDelete={() => handleDocDelete("gstCertificate")}
          />
        </Field>
      </div>
    </div>
  );
}

function SuccessScreen({ onReset, centerCode, currentApproverLevel, totalLevels }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
        <Check className="h-8 w-8 text-emerald-600" />
      </div>
      <h2 className="text-xl font-bold text-slate-900">CC Onboarded Successfully!</h2>
      <p className="mt-2 max-w-sm text-sm text-slate-500">
        {centerCode && (
          <>
            <span className="font-semibold text-slate-600">{centerCode}</span> has been submitted for review
            {currentApproverLevel && totalLevels ? ` (level ${currentApproverLevel} of ${totalLevels})` : ""}.{" "}
          </>
        )}
        The operations team will verify the details within 24–48 hours.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-6 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
      >
        Onboard Another CC
      </button>
    </div>
  );
}

export default function LabOnboarding({
  readOnly = false,
  onExit = null,
  onApprove = null,
  onReject = null,
  approving = false,
  rejecting = false,
} = {}) {
  const [state, localDispatch] = useReducer(reducer, initialState);
  const { form, currentStep, status: localStatus, errorMessage: localErrorMessage } = state;
  const step = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;

  const reduxDispatch = useDispatch();
  const { draftKey, stepStatus, submit: submitState, workflowChain, isDirty } = useSelector(selectCcOnboarding);
  const pendingEdit = useSelector(selectPendingEdit);
  const [searchParams] = useSearchParams();

  const dispatch = useCallback(
    (action) => {
      localDispatch(action);
      if (DIRTYING_ACTION_TYPES.has(action.type)) reduxDispatch(markDirty());
    },
    [localDispatch, reduxDispatch]
  );

  useNavigationGuard();
  const { navigateSafely, isPromptOpen, confirmLeave, cancelLeave } = useConfirmedNavigate();
  const [pendingExit, setPendingExit] = useState(false);
  const handleExitClick = () => {
    if (!onExit) {
      navigateSafely("/dashboard");
      return;
    }
    if (isDirty) {
      setPendingExit(true);
    } else {
      onExit();
    }
  };

  const resumeKey = searchParams.get("draftKey") || draftKey;
  const editCenterCode = searchParams.get("centerCode") || null;
  const editingCenterCodeRef = useRef(editCenterCode);
  const hasResumed = useRef(false);
  const [editRecordStatusCode, setEditRecordStatusCode] = useState(null);
  const [approvalHistory, setApprovalHistory] = useState([]);

  const reduxDetail = useSelector((s) => s.ccOnboarding?.detail?.data);
  useEffect(() => {
    if (reduxDetail) {
      const extracted = extractApprovalHistory(reduxDetail);
      if (extracted.length) {
        setApprovalHistory(extracted);
      }
    }
  }, [reduxDetail]);

  useEffect(() => {
    reduxDispatch(fetchWorkflowChain());
    if (editCenterCode && !hasResumed.current) {
      hasResumed.current = true;
      reduxDispatch(fetchDetail(editCenterCode))
        .unwrap()
        .then((detailResponse) => {
          setEditRecordStatusCode(detailResponse?.statusCode ?? null);
          const historyFromDetail = extractApprovalHistory(detailResponse);
          if (historyFromDetail.length) {
            setApprovalHistory(historyFromDetail);
          }
          const parkedDraftKey = detailResponse?.pendingEdit?.draftKey || null;
          reduxDispatch(beginEdit({ draftKey: parkedDraftKey }));

          const mappedLive = mapDraftToForm(detailResponse);
          if (parkedDraftKey) {
            if (Object.keys(mappedLive).length) {
              localDispatch({ type: "HYDRATE_FORM", payload: mappedLive });
            }
            reduxDispatch(fetchDraft(parkedDraftKey))
              .unwrap()
              .then((draftResponse) => {
                const mappedDraft = mapDraftToForm(draftResponse);
                if (Object.keys(mappedDraft).length) {
                  localDispatch({ type: "HYDRATE_FORM", payload: mappedDraft });
                }
              })
              .catch(() => { });
            reduxDispatch(fetchDraftProgress(parkedDraftKey));
            return;
          }

          const mappedForm = mapDraftToForm(detailResponse);
          if (Object.keys(mappedForm).length) {
            localDispatch({ type: "HYDRATE_FORM", payload: mappedForm });
          }
        })
        .catch(() => { });
    } else if (resumeKey && !hasResumed.current) {
      hasResumed.current = true;
      reduxDispatch(fetchDraft(resumeKey))
        .unwrap()
        .then((draftResponse) => {
          const mappedForm = mapDraftToForm(draftResponse);
          if (Object.keys(mappedForm).length) {
            localDispatch({ type: "HYDRATE_FORM", payload: mappedForm });
          }
        })
        .catch(() => { });
      reduxDispatch(fetchDraftProgress(resumeKey));
    }
  }, []);

  const setField = useCallback(
    (section) => (field, value) => dispatch({ type: "SET_FIELD", payload: { section, field, value } }),
    [dispatch]
  );

  const currentStepNo = STEP_ID_TO_NO[step.id];
  const isSaving = !!stepStatus[currentStepNo]?.saving;
  const stepSaveError = stepStatus[currentStepNo]?.error;

  const isGenuineEditRecord = !!editingCenterCodeRef.current && editRecordStatusCode !== STATUS_CODES.DRAFT;

  const currentSectionId = STEPS[currentStep]?.id;
  const missingOnThisStep = readOnly ? [] : missingRequired(currentSectionId, form[currentSectionId]);

  const validateCurrentStep = () => {
    localDispatch({ type: "CLEAR_ERROR" });

    if (currentSectionId === "labInfo") {
      if (form.labInfo.pinCode && !/^\d{6}$/.test(String(form.labInfo.pinCode).trim())) {
        localDispatch({
          type: "SUBMIT_ERROR",
          payload: "Pin Code must be exactly 6 digits.",
        });
        return false;
      }
      if (form.labInfo.userRole === "Swasthya Mitra Diagnostic" && !form.labInfo.eclinicCode) {
        localDispatch({
          type: "SUBMIT_ERROR",
          payload: "Please select an ECLINIC Name.",
        });
        return false;
      }
      if (form.labInfo.userRole === "Cooperate Diagnostics" && !form.labInfo.corporateCode) {
        localDispatch({
          type: "SUBMIT_ERROR",
          payload: "Please select a Corporate.",
        });
        return false;
      }
    }

    if (currentSectionId === "contacts") {
      const contactErrors = validateContacts(form.contacts);
      if (contactErrors.length > 0) {
        localDispatch({
          type: "SUBMIT_ERROR",
          payload: contactErrors.join("\n"),
        });
        return false;
      }
    }

    if (currentSectionId === "testTimings") {
      const timings = form.testTimings.timings || [];
      if (!timings.length) {
        localDispatch({
          type: "SUBMIT_ERROR",
          payload: "Please configure at least one timing slot.",
        });
        return false;
      }
      for (let i = 0; i < timings.length; i++) {
        const t = timings[i];
        const days = Array.isArray(t.days) && t.days.length ? t.days : t.day ? [t.day] : [];
        if (!days.length) {
          localDispatch({
            type: "SUBMIT_ERROR",
            payload: `Timing Slot #${i + 1}: Please select at least one day.`,
          });
          return false;
        }
        if (!t.isClosed && (!t.opensAt || !t.closesAt)) {
          localDispatch({
            type: "SUBMIT_ERROR",
            payload: `Timing Slot #${i + 1}: Please specify Open and Close times.`,
          });
          return false;
        }
      }
    }

    if (missingOnThisStep.length) {
      localDispatch({
        type: "SUBMIT_ERROR",
        payload: `Please fill in ${missingOnThisStep.map((f) => FIELD_LABELS[f] || f).join(", ")} before continuing.`,
      });
      return false;
    }

    return true;
  };

  const saveCurrentStep = async () => {
    if (readOnly) return false;
    if (currentSectionId === "approvalHistory") return true;

    if (!validateCurrentStep()) {
      return false;
    }

    await reduxDispatch(
      saveWizardStep({
        stepNo: currentStepNo,
        form,
        centerCode: editingCenterCodeRef.current || undefined,
        complete: true,
      })
    );
    return true;
  };

  const handleNext = async () => {
    if (!(await saveCurrentStep())) return;

    if (isLastStep) {
      handleSubmit();
    } else {
      dispatch({ type: "GO_NEXT" });
    }
  };

  const handleTabSelect = async (idx) => {
    if (!readOnly) {
      const isValid = validateCurrentStep();
      if (isValid) {
        await saveCurrentStep();
      }
    }
    dispatch({ type: "SET_STEP", payload: idx });
  };

  const handleSaveChanges = async () => {
    if (!(await saveCurrentStep())) return;
    await handleSubmit();
  };

  const handleSubmit = async () => {
    const isGenuineEdit = isGenuineEditRecord;
    const result = isGenuineEdit
      ? await reduxDispatch(updateOnboarding({ centerCode: editingCenterCodeRef.current, form }))
      : await reduxDispatch(submitOnboarding({ draftKey: resumeKey, form }));
    const succeeded = isGenuineEdit
      ? updateOnboarding.fulfilled.match(result)
      : submitOnboarding.fulfilled.match(result);
    if (succeeded) {
      localDispatch({ type: "SUBMIT_SUCCESS" });
    } else {
      localDispatch({ type: "SUBMIT_ERROR", payload: result.payload || "Something went wrong" });
    }
  };

  const handleReset = () => {
    reduxDispatch(resetWizardAction());
    localDispatch({ type: "RESET_FORM" });
  };

  if (localStatus === "success" || submitState.status === "succeeded") {
    return (
      <div className="min-h-screen w-full bg-slate-50 px-6 py-10 flex justify-center">
        <div className="w-full max-w-5xl rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
          <SuccessScreen
            centerCode={submitState.centerCode}
            currentApproverLevel={submitState.currentApproverLevel}
            totalLevels={workflowChain.data?.length || TOTAL_APPROVAL_LEVELS}
            onReset={handleReset}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-slate-50">
      <div className="w-full bg-white border-b border-slate-200">
        <TopTabs steps={STEPS} currentStep={currentStep} onSelect={handleTabSelect} />
      </div>

      <div className="w-full bg-slate-50">
        <div className="p-4">
          {step.id === "labInfo" && <LabInfoStep data={form.labInfo} setField={setField("labInfo")} readOnly={readOnly} />}
          {step.id === "contacts" && <ContactsStep contacts={form.contacts} dispatch={dispatch} readOnly={readOnly} />}
          {step.id === "testTimings" && <TestTimingsStep data={form.testTimings} dispatch={dispatch} readOnly={readOnly} />}
          {step.id === "payment" && (
            <PaymentStep
              data={form.payment}
              setField={setField("payment")}
              diagnosticType={form.labInfo.diagnosticType}
              automapping={form.labInfo.automapping}
              pinCode={form.labInfo.pinCode}
              dispatch={dispatch}
              readOnly={readOnly}
            />
          )}
          {step.id === "compliance" && <ComplianceStep data={form.compliance} setField={setField("compliance")} readOnly={readOnly} />}
          {step.id === "bank" && <BankStep data={form.bank} setField={setField("bank")} readOnly={readOnly} />}
          {step.id === "documents" && <DocumentsStep data={form.documents} setField={setField("documents")} readOnly={readOnly} />}
          {step.id === "approvalHistory" && (
            <div className="flex justify-center">
              <div className="w-full max-w-5xl">
                <ApprovalHistoryTable
                  history={approvalHistory}
                  centerCode={editCenterCode || editingCenterCodeRef.current || ""}
                />
              </div>
            </div>
          )}

          {!readOnly && (stepSaveError || localStatus === "error" || submitState.status === "failed") && (
            <div className="flex justify-center">
              <div className="mt-4 flex w-full max-w-[40%] flex-col gap-1 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">
                {localStatus === "error" && localErrorMessage
                  ? localErrorMessage.split("\n").map((msg, i) => (
                    <span key={i} className="block">{msg}</span>
                  ))
                  : stepSaveError || submitState.error || localErrorMessage}
              </div>
            </div>
          )}

          {!readOnly && (
            <div className="flex justify-center mt-4">
              <div className="flex items-center justify-between w-full max-w-[40%] border-t border-slate-200 pt-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleExitClick}
                    className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={currentStep === 0}
                    onClick={() => dispatch({ type: "GO_BACK" })}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-400 px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" /> Back
                  </button>
                </div>

                <div className="flex gap-1.5">
                  {STEPS.map((_, idx) => (
                    <span
                      key={idx}
                      className={`h-1.5 rounded-full transition-all ${
                        idx === currentStep ? "w-6 bg-indigo-600" : idx < currentStep ? "w-1.5 bg-emerald-400" : "w-1.5 bg-slate-300"
                      }`}
                    />
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  {isGenuineEditRecord && !isLastStep && (
                    <button
                      type="button"
                      onClick={handleSaveChanges}
                      disabled={isSaving || submitState.status === "submitting"}
                      className="flex items-center gap-1.5 rounded-lg border border-indigo-600 px-4 py-2.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50 disabled:opacity-60"
                    >
                      {submitState.status === "submitting" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                        </>
                      ) : (
                        <>Save Changes</>
                      )}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={isSaving || submitState.status === "submitting"}
                    className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {submitState.status === "submitting" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                      </>
                    ) : isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                      </>
                    ) : isLastStep ? (
                      <>{isGenuineEditRecord ? "Save Changes" : "Submit CC"} <ChevronRight className="h-4 w-4" /></>
                    ) : (
                      <>Continue <ChevronRight className="h-4 w-4" /></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {readOnly && (
            <div className="flex justify-center mt-4">
              <div className="flex items-center justify-between w-full max-w-[40%] border-t border-slate-200 pt-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleExitClick}
                    className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={currentStep === 0}
                    onClick={() => dispatch({ type: "GO_BACK" })}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-400 px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" /> Previous Tab
                  </button>
                </div>
                <div className="flex gap-1.5">
                  {STEPS.map((_, idx) => (
                    <span
                      key={idx}
                      className={`h-1.5 rounded-full transition-all ${
                        idx === currentStep ? "w-6 bg-indigo-600" : idx < currentStep ? "w-1.5 bg-emerald-400" : "w-1.5 bg-slate-300"
                      }`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  disabled={isLastStep}
                  onClick={() => dispatch({ type: "GO_NEXT" })}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-400 px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next Tab <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmLeaveModal
        open={isPromptOpen || pendingExit}
        onStay={() => {
          cancelLeave();
          setPendingExit(false);
        }}
        onLeave={() => {
          if (pendingExit) {
            setPendingExit(false);
            onExit();
          } else {
            confirmLeave();
          }
        }}
      />
    </div>
  );
}
